import React, { useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, PerspectiveCamera } from '@react-three/drei';
import './VehicleViewer.css';

export function checkWebGLSupport() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return false;
    canvas.remove();
    return true;
  } catch {
    return false;
  }
}

export function isLowEndDevice() {
  if (!checkWebGLSupport()) return true;
  if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) return true;
  if (navigator.deviceMemory && navigator.deviceMemory <= 2) return true;
  return false;
}

export const VehicleFallbackImage = () => (
  <div className="vehicle-viewer-container">
    <img
      src={`${import.meta.env.BASE_URL}ev_car_image.png`}
      alt="Demo electric vehicle"
      className="vehicle-fallback-image"
    />
    <div className="viewer-instructions">
      <p>🖼️ Image mode — 3D not supported on this device</p>
    </div>
  </div>
);

// Simple Car Model using geometric primitives
const CarModel = ({ onComponentClick, highlightedComponent }) => {
  const carRef = useRef();
  const [hovered, setHovered] = useState(null);

  // Subtle idle animation
  useFrame((state) => {
    if (carRef.current) {
      carRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.05;
    }
  });

  const getComponentColor = (component) => {
    if (highlightedComponent === component) {
      return '#66B4C4'; // Accent when selected
    }
    if (hovered === component) {
      return '#2A94AB'; // Blue 400 when hovered
    }
    return '#23383F'; // Default dark blue
  };

  return (
    <group ref={carRef}>
      {/* Car Body */}
      <mesh position={[0, 0.5, 0]}>
        <boxGeometry args={[3, 1, 1.5]} />
        <meshStandardMaterial
          color="#2d3748"
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Car Roof */}
      <mesh position={[0, 1.2, 0]}>
        <boxGeometry args={[2, 0.8, 1.4]} />
        <meshStandardMaterial
          color="#1a202c"
          metalness={0.8}
          roughness={0.2}
        />
      </mesh>

      {/* Battery Pack (bottom - clickable hotspot) */}
      <mesh
        position={[0, -0.3, 0]}
        onClick={() => onComponentClick('battery')}
        onPointerOver={() => setHovered('battery')}
        onPointerOut={() => setHovered(null)}
      >
        <boxGeometry args={[2.5, 0.4, 1.2]} />
        <meshStandardMaterial
          color={getComponentColor('battery')}
          emissive={highlightedComponent === 'battery' ? '#66B4C4' : '#000000'}
          emissiveIntensity={highlightedComponent === 'battery' ? 0.5 : 0}
          metalness={0.6}
          roughness={0.3}
        />
      </mesh>

      {/* Motor (front - clickable hotspot) */}
      <mesh
        position={[1.3, 0.2, 0]}
        onClick={() => onComponentClick('motor')}
        onPointerOver={() => setHovered('motor')}
        onPointerOut={() => setHovered(null)}
      >
        <cylinderGeometry args={[0.4, 0.4, 0.8, 16]} />
        <meshStandardMaterial
          color={getComponentColor('motor')}
          emissive={highlightedComponent === 'motor' ? '#66B4C4' : '#000000'}
          emissiveIntensity={highlightedComponent === 'motor' ? 0.5 : 0}
          metalness={0.7}
          roughness={0.2}
        />
      </mesh>

      {/* Charging Port (front-right - clickable hotspot) */}
      <mesh
        position={[1.2, 0.8, 0.6]}
        onClick={() => onComponentClick('charging')}
        onPointerOver={() => setHovered('charging')}
        onPointerOut={() => setHovered(null)}
      >
        <boxGeometry args={[0.2, 0.3, 0.1]} />
        <meshStandardMaterial
          color={getComponentColor('charging')}
          emissive={highlightedComponent === 'charging' ? '#66B4C4' : '#000000'}
          emissiveIntensity={highlightedComponent === 'charging' ? 0.5 : 0}
          metalness={0.5}
          roughness={0.4}
        />
      </mesh>

      {/* Wheels */}
      {[
        [-1, -0.1, 0.8],   // Front Left
        [-1, -0.1, -0.8],  // Front Right
        [1, -0.1, 0.8],    // Rear Left
        [1, -0.1, -0.8]    // Rear Right
      ].map((pos, i) => (
        <group key={i} position={pos}>
          <mesh rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.4, 0.4, 0.3, 16]} />
            <meshStandardMaterial
              color="#1a1a1a"
              metalness={0.9}
              roughness={0.1}
            />
          </mesh>
        </group>
      ))}

      {/* Headlights */}
      <mesh position={[1.5, 0.6, 0.5]}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshStandardMaterial
          color="#ffffff"
          emissive="#ffffff"
          emissiveIntensity={0.3}
        />
      </mesh>
      <mesh position={[1.5, 0.6, -0.5]}>
        <sphereGeometry args={[0.15, 16, 16]} />
        <meshStandardMaterial
          color="#ffffff"
          emissive="#ffffff"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Taillights */}
      <mesh position={[-1.5, 0.6, 0.5]}>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial
          color="#ff0000"
          emissive="#ff0000"
          emissiveIntensity={0.2}
        />
      </mesh>
      <mesh position={[-1.5, 0.6, -0.5]}>
        <sphereGeometry args={[0.12, 16, 16]} />
        <meshStandardMaterial
          color="#ff0000"
          emissive="#ff0000"
          emissiveIntensity={0.2}
        />
      </mesh>
    </group>
  );
};

// Ground Plane
const Ground = () => {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]} receiveShadow>
      <planeGeometry args={[20, 20]} />
      <meshStandardMaterial
        color="#0B1418"
        metalness={0.3}
        roughness={0.8}
        opacity={0.5}
        transparent
      />
    </mesh>
  );
};

// Component Labels
const ComponentLabel = ({ position, label, visible }) => {
  if (!visible) return null;

  return (
    <group position={position}>
      <mesh>
        <planeGeometry args={[1.5, 0.4]} />
        <meshBasicMaterial
          color="#142329"
          opacity={0.9}
          transparent
        />
      </mesh>
      {/* Text would go here - simplified for now */}
    </group>
  );
};

const VehicleFallback = () => (
  <div className="vehicle-viewer-container">
    <img
      src={`${import.meta.env.BASE_URL}ev_car_image.png`}
      alt="Demo electric vehicle"
      className="vehicle-fallback-image"
    />
    <div className="viewer-instructions">
      <p>🖼️ Image mode — 3D not supported on this device</p>
    </div>
  </div>
);

const VehicleViewer = ({ onComponentClick, highlightedComponent }) => {
  const [use3D] = useState(() => !isLowEndDevice());

  if (!use3D) return <VehicleFallback />;

  return (
    <div className="vehicle-viewer-container">
      <Canvas
        shadows
        className="vehicle-canvas"
        gl={{ antialias: true, alpha: true }}
      >
        <PerspectiveCamera makeDefault position={[5, 3, 5]} fov={50} />

        {/* Lighting */}
        <ambientLight intensity={0.4} />
        <directionalLight
          position={[10, 10, 5]}
          intensity={1}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <pointLight position={[-10, 5, -10]} intensity={0.5} color="#66B4C4" />
        <pointLight position={[10, 5, 10]} intensity={0.3} color="#2A94AB" />
        <spotLight
          position={[0, 10, 0]}
          angle={0.3}
          penumbra={1}
          intensity={0.5}
          castShadow
        />

        {/* Environment */}
        <Environment preset="night" />

        {/* Controls */}
        <OrbitControls
          enablePan={true}
          enableZoom={true}
          minDistance={3}
          maxDistance={15}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI / 2}
          autoRotate={!highlightedComponent}
          autoRotateSpeed={0.5}
        />

        {/* Scene */}
        <Ground />
        <CarModel
          onComponentClick={onComponentClick}
          highlightedComponent={highlightedComponent}
        />

        {/* Grid Helper */}
        <gridHelper args={[20, 20, '#66B4C4', '#23383F']} position={[0, -0.49, 0]} />
      </Canvas>

      {/* Component Legend */}
      <div className="component-legend">
        <div className="legend-item">
          <div className="legend-color" style={{ background: '#66B4C4' }}></div>
          <span>Selected</span>
        </div>
        <div className="legend-item">
          <div className="legend-color" style={{ background: '#2A94AB' }}></div>
          <span>Hover</span>
        </div>
      </div>

      {/* Instructions */}
      <div className="viewer-instructions">
        <p>🖱️ Click components • Drag to rotate • Scroll to zoom</p>
      </div>
    </div>
  );
};

export default VehicleViewer;
